import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Where the settings sidebar's back arrow returns to.
 *
 * Settings is reachable from the sidebar, the profile drawer, global search,
 * dashboard banners and notifications, so no single caller can hand it a
 * "from" location. Instead the app `Layout` records every page it shows that
 * is not Settings, and the settings sidebar reads the last one back.
 *
 * Session-scoped: a fresh tab that opens Settings directly has nothing stored
 * and falls back to the space's home.
 */
const STORAGE_KEY = "settings:return-to";

/**
 * Only an in-app path is a place to go back to. The value is used as a link
 * target, and `//host` (or `/\host`, which browsers read the same way) is a
 * protocol-relative URL to another site — a pathname can start that way
 * (`app.example//evil.example`), so it is checked, not assumed.
 */
const isAppPath = (value: string | null): value is string =>
  !!value && value.startsWith("/") && !/^\/[/\\]/.test(value);

/** Called by the main app `Layout` — records each page it shows. */
export const useRememberAppLocation = (enabled = true) => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!enabled) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, `${pathname}${search}`);
    } catch {
      // Storage blocked (private mode, quota) — the back arrow falls back.
    }
  }, [enabled, pathname, search]);
};

export const getAppReturnPath = (fallback: string): string => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return isAppPath(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
};
